import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../mockDB';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const DeactivateConfirmModal = ({ onConfirm, onCancel }) => {
  const cancelBtnRef = useRef(null);
  useEffect(() => { cancelBtnRef.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4 z-50">
      <div className="modal-panel">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
            <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-2xl text-amber-500" />
          </div>
          <h3 className="text-lg font-bold text-brand-navy mb-2">Deactivate Voucher?</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            This voucher will be deactivated and clients will no longer be able to redeem it. Are you sure?
          </p>
        </div>
        <div className="flex gap-3">
          <button ref={cancelBtnRef} onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
          <button onClick={onConfirm} className="btn-primary flex-1">Confirm</button>
        </div>
      </div>
    </div>
  );
};

const DiscountBadge = ({ voucher }) => {
  const label = voucher.discount_type === 'full' ? '100% Free' : `${voucher.value}% Off`;
  return (
    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold">
      <SafeIcon icon={FiIcons.FiTag} className="text-xs" />
      {label}
    </span>
  );
};

export const VoucherManagement = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);

  useEffect(() => { loadVouchers(); }, []);

  const loadVouchers = async () => {
    const data = await db.getAllVouchers();
    setVouchers(data);
    setLoading(false);
  };

  const handleToggleRequest = (voucher) => {
    if (voucher.is_active) {
      setConfirmDeactivateId(voucher.id);
    } else {
      executeToggle(voucher.id);
    }
  };

  const executeToggle = async (id) => {
    setConfirmDeactivateId(null);
    setTogglingId(id);
    const updated = await db.toggleVoucherStatus(id);
    setVouchers((prev) => prev.map((v) => v.id === id ? updated : v));
    setTogglingId(null);
  };

  const activeCount = vouchers.filter((v) => v.is_active).length;
  const totalRedeemed = vouchers.reduce((sum, v) => sum + (v.times_redeemed || 0), 0);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading vouchers...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-navy">Voucher Management</h2>
          <p className="text-sm text-slate-500">Manage discount codes and promotional offers</p>
        </div>
        <div className="flex gap-3">
          <div className="text-center bg-white border border-slate-200 rounded-lg px-4 py-2">
            <p className="text-lg font-bold text-brand-navy">{activeCount}</p>
            <p className="text-xs text-slate-500">Active</p>
          </div>
          <div className="text-center bg-white border border-slate-200 rounded-lg px-4 py-2">
            <p className="text-lg font-bold text-brand-navy">{totalRedeemed}</p>
            <p className="text-xs text-slate-500">Total Uses</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <tr>
                <th className="px-4 md:px-6 py-3 font-semibold">Code</th>
                <th className="px-4 md:px-6 py-3 font-semibold">Discount</th>
                <th className="px-4 md:px-6 py-3 font-semibold hidden sm:table-cell">Label</th>
                <th className="px-4 md:px-6 py-3 font-semibold text-center hidden sm:table-cell">Times Redeemed</th>
                <th className="px-4 md:px-6 py-3 font-semibold text-center">Status</th>
                <th className="px-4 md:px-6 py-3 font-semibold text-center">Toggle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vouchers.map((voucher) => (
                <tr key={voucher.id} className={`hover:bg-slate-50 transition-colors ${!voucher.is_active ? 'opacity-60' : ''}`}>
                  <td className="px-4 md:px-6 py-4">
                    <code className="bg-slate-100 text-brand-navy font-bold px-2 py-1 rounded text-xs tracking-widest">
                      {voucher.code}
                    </code>
                  </td>
                  <td className="px-4 md:px-6 py-4"><DiscountBadge voucher={voucher} /></td>
                  <td className="px-4 md:px-6 py-4 text-slate-600 hidden sm:table-cell">{voucher.label || '—'}</td>
                  <td className="px-4 md:px-6 py-4 text-center hidden sm:table-cell">
                    <div className="flex items-center justify-center gap-1.5">
                      <SafeIcon icon={FiIcons.FiUsers} className="text-slate-400 text-xs" />
                      <span className="font-semibold text-brand-navy">{voucher.times_redeemed || 0}</span>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-4 text-center">
                    <span className={`status-badge ${voucher.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      {voucher.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4 text-center">
                    <button
                      onClick={() => handleToggleRequest(voucher)}
                      disabled={togglingId === voucher.id}
                      title={voucher.is_active ? 'Deactivate' : 'Activate'}
                      className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none ${voucher.is_active ? 'bg-emerald-500' : 'bg-slate-300'} ${togglingId === voucher.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform ${voucher.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <SafeIcon icon={FiIcons.FiInfo} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          Deactivating a voucher prevents new redemptions but does not affect clients who have already applied it. Clients can only redeem one voucher per account.
        </p>
      </div>

      {confirmDeactivateId && (
        <DeactivateConfirmModal
          onConfirm={() => executeToggle(confirmDeactivateId)}
          onCancel={() => setConfirmDeactivateId(null)}
        />
      )}
    </div>
  );
};